import React from 'react';
import { ChatBubbleLeftRightIcon } from '@heroicons/react/24/outline';

interface Testimonial {
  id: string;
  quote: string;
  name: string;
  affiliation: string;
}

const Testimonials: React.FC = () => {
  // Testimonials from Language Intelligence 2025
  const testimonials: Testimonial[] = [
    {
      id: '1',
      quote: "Language Intelligence 2025 - a conference I should attend? Absolutely! It was fantastic!",
      name: "Georg Lanzinger",
      affiliation: "Centre for Risk & Crisis Management"
    },
    {
      id: '2',
      quote: "This event shifted the focus from efficiency and cost-cutting to viewing \"Language Intelligence\" as a key driver of value.",
      name: "Kirti Vashee",
      affiliation: "Translated"
    },
    {
      id: '3',
      quote: "What inspired me most was the shared understanding in the room: language technologies are no longer \"nice to have\", they are becoming essential infrastructure.",
      name: "Fatemeh-Shirin Asgari",
      affiliation: "Austrian Rail Infrastructure"
    },
    {
      id: '4',
      quote: "The morning was reserved for folks who spoke about using tech in the invisible systems and infrastructure that underpin much of society. This was excellent — more of this for other events, please! That's the mix that keeps the conference circuit going and the inspiration flowing!",
      name: "Gabriel Karandyšovský",
      affiliation: "Argos Multilingual"
    },
    {
      id: '5',
      quote: "What won't be automated is human language intelligence: the ability to interpret, create, strategize, and build meaning across cultures. That's where the industry's future value lies. The key is not in defending the past. We're stepping into a much larger, open space of possibility, where multilingual expertise becomes a competitive growth engine.",
      name: "Diego Cresceri",
      affiliation: "Creative Words"
    },
    {
      id: '6',
      quote: "LT-Innovate has a unique vibe: small enough to be intimate, big enough to spark ideas, and refreshingly free of hype.",
      name: "Coral Diez Carbajo",
      affiliation: "Acclaro"
    },
    {
      id: '7',
      quote: "One thinks 2025 was disappointing, and then you get two totally unexpected gems in two weeks. My 3D team in Bangalore, and #LI2025.",
      name: "Anna Fellet",
      affiliation: "Uber"
    },
    {
      id: '8',
      quote: "My mind has been filled with great ideas because of LT-Innovate's Language Intelligence Summit 2025. It was so satisfying to see in every session people challenging the concept of language intelligence and defining it in their own way.",
      name: "Ziyi Zhang",
      affiliation: "Zoetis"
    },
    {
      id: '9',
      quote: "The Language Intelligence Summit #LI2025 felt like a three-day brain massage: big ideas, bold questions, and the kind of conversations you want to keep having long after the coffee break ends.",
      name: "Bea Verdasco Vidal",
      affiliation: "Trendyol"
    },
    {
      id: '10',
      quote: "We did it again at #LI2025. The intensity of ideas, the generosity of dialogue, and the sense that something truly significant is unfolding in our field was almost immersive at this year's Language Intelligence Conference.",
      name: "Britta Aagaard",
      affiliation: "Semantix/TransPerfect"
    },
    {
      id: '11',
      quote: "Planning your 2026 conference calendar? LT-Innovate should be on your list.",
      name: "Libor Safar",
      affiliation: "Argos Multilingual"
    },
    {
      id: '12',
      quote: "The Language Intelligence Conference 2025 truly broadened my horizons regarding the application of language technologies across different business domains. The presentations were insightful and inspiring, bringing together a range of viewpoints and sparking engaging discussions with the audience. I am truly honoured and grateful to have participated in this event.",
      name: "Chen Jessica Zhiyi",
      affiliation: "University of Vienna"
    },
    {
      id: '13',
      quote: "I've had the chance to attend the Language Intelligence conference for the past two years as a student from the University of Vienna. The organizers kindly gave us free passes for the two-day event, which featured many presentations on language technologies, including potential applications of Large Language Models, AI system quality, and the importance of user value. The conference was a great opportunity to learn about different use cases and research in the field. I especially enjoyed seeing the range of projects and ideas, which inspired me for my own future work possibilities after graduation. Meeting industry professionals from around the world and hearing from experts was also extremely valuable. I'm very thankful to the organizers for this experience and would recommend the conference to anyone interested in AI-powered language technologies.",
      name: "Dimitra Flouli",
      affiliation: "University of Vienna"
    },
    {
      id: '14',
      quote: "Language Intelligence 2025 provided a unique combination of industry, research, and public administration experts on language technologies, which made for very interesting and inspiring talks and discussions. Such diverse and fruitful networking cannot easily be found elsewhere and our many students that were allowed to join fully enjoyed the event and left with lots of new ideas for their projects and careers.",
      name: "Dagmar Gromann",
      affiliation: "University of Vienna"
    }
  ];

  return (
    <section className="py-24 bg-gradient-to-br from-gray-50 to-white">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-16">
          <h2 className="text-3xl font-bold bg-gradient-to-r from-[#00529B] to-[#E31D3C] bg-clip-text text-transparent sm:text-4xl">
            What Attendees Say
          </h2>
          <p className="mt-4 text-lg text-gray-600 max-w-3xl mx-auto">
            Voices from Language Intelligence 2025
          </p>
        </div>

        {/* Masonry-style grid for testimonials */}
        <div className="columns-1 md:columns-2 lg:columns-3 gap-6 space-y-6">
          {testimonials.map((testimonial) => (
            <div
              key={testimonial.id}
              className="break-inside-avoid bg-white rounded-xl p-6 shadow-md hover:shadow-lg transition-shadow duration-300 border border-gray-100"
            >
              <div className="flex items-start gap-3 mb-4">
                <ChatBubbleLeftRightIcon className="w-6 h-6 text-[#00529B] flex-shrink-0 mt-1" />
                <blockquote className="text-gray-700 italic leading-relaxed">
                  "{testimonial.quote}"
                </blockquote>
              </div>
              <div className="flex items-center gap-3 pt-4 border-t border-gray-100">
                <div className="w-10 h-10 rounded-full bg-gradient-to-br from-[#00529B] to-[#E31D3C] flex items-center justify-center text-white font-bold text-sm">
                  {testimonial.name.split(' ').map(n => n[0]).join('').slice(0, 2)}
                </div>
                <div>
                  <p className="font-semibold text-gray-900">{testimonial.name}</p>
                  <p className="text-sm text-gray-500">{testimonial.affiliation}</p>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

export default Testimonials;
